import type { ReactNode } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router';
import { cn } from './cn';
import { Icon } from './icon';
import { Menu, MenuButton, MenuLabel, MenuLink, MenuSeparator } from './menu';
import { useUiStrings } from './strings';

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: string;
}

export interface WorkspaceOption {
  id: string;
  label: string;
  hint?: string;
}

export interface AppShellProps {
  brand: ReactNode;
  /** Always-visible bar (FR-NAV-001). */
  primary: readonly NavItem[];
  /** "More" menu (FR-NAV-002) … */
  overflow: readonly NavItem[];
  /** … and, after a separator, its actions (FR-NAV-003). */
  actions: readonly NavItem[];
  mainNavLabel: string;
  workspace: {
    label: string;
    currentId: string | null;
    currentLabel: string;
    options: readonly WorkspaceOption[];
    onSelect: (id: string) => void;
  };
  user: {
    label: string;
    name: string;
    email: string;
    settings: readonly NavItem[];
    settingsLabel: string;
    logoutLabel: string;
    onLogout: () => void;
  };
  children: ReactNode;
}

/** `/` matches only itself; other items also own their sub-pages (`/customers/5`). */
const isActive = (to: string, pathname: string) =>
  matchPath({ path: to, end: to === '/' }, pathname) !== null;

/**
 * The application frame — rendered exactly once, by the root layout route (ADR 0009). Every
 * menu entry comes from the module registry via props; no page draws its own navigation.
 */
export function AppShell({
  brand,
  primary,
  overflow,
  actions,
  mainNavLabel,
  workspace,
  user,
  children,
}: AppShellProps) {
  const { pathname } = useLocation();
  const s = useUiStrings();
  const overflowActive = [...overflow, ...actions].some((i) => isActive(i.to, pathname));

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b border-border bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-screen-2xl items-center gap-4 px-4">
          <div className="shrink-0 text-lg font-bold tracking-tight text-brand-700">{brand}</div>

          <nav aria-label={mainNavLabel} className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
            {primary.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive: active }) =>
                  cn(
                    'flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    active ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:bg-canvas hover:text-ink',
                  )
                }
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            ))}
            {(overflow.length > 0 || actions.length > 0) && (
              <Menu
                label={s.moreSections}
                align="start"
                trigger={
                  <button
                    type="button"
                    className={cn(
                      'flex shrink-0 items-center rounded-lg px-3 py-2 text-ink-muted hover:bg-canvas hover:text-ink',
                      overflowActive && 'bg-brand-50 text-brand-700',
                    )}
                  >
                    <Icon name="ellipsis" className="size-5" />
                  </button>
                }
              >
                {overflow.map((item) => (
                  <MenuLink key={item.key} to={item.to} icon={item.icon} active={isActive(item.to, pathname)}>
                    {item.label}
                  </MenuLink>
                ))}
                {actions.length > 0 && overflow.length > 0 && <MenuSeparator />}
                {actions.map((item) => (
                  <MenuLink key={item.key} to={item.to} icon={item.icon} active={isActive(item.to, pathname)}>
                    {item.label}
                  </MenuLink>
                ))}
              </Menu>
            )}
          </nav>

          <Menu
            label={workspace.label}
            trigger={
              <button
                type="button"
                className="flex max-w-56 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-canvas"
              >
                <span className="truncate font-medium" data-testid="current-workspace">
                  {workspace.currentLabel}
                </span>
                <Icon name="chevron-down" className="size-4 shrink-0 text-ink-muted" />
              </button>
            }
          >
            <MenuLabel>{workspace.label}</MenuLabel>
            {workspace.options.map((o) => (
              <MenuButton
                key={o.id}
                onSelect={() => workspace.onSelect(o.id)}
                active={o.id === workspace.currentId}
              >
                <span className="flex flex-col">
                  <span>{o.label}</span>
                  {o.hint && <span className="text-xs text-ink-muted">{o.hint}</span>}
                </span>
              </MenuButton>
            ))}
          </Menu>

          <Menu
            label={user.label}
            trigger={
              <button
                type="button"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-white"
              >
                {user.name.trim().charAt(0).toUpperCase() || '?'}
              </button>
            }
          >
            <div className="px-2.5 py-2">
              <p className="text-sm font-semibold">{user.name}</p>
              <p className="text-xs text-ink-muted">{user.email}</p>
            </div>
            {user.settings.length > 0 && (
              <>
                <MenuSeparator />
                <MenuLabel>{user.settingsLabel}</MenuLabel>
                {user.settings.map((item) => (
                  <MenuLink key={item.key} to={item.to} icon={item.icon} active={isActive(item.to, pathname)}>
                    {item.label}
                  </MenuLink>
                ))}
              </>
            )}
            <MenuSeparator />
            <MenuButton icon="log-out" onSelect={user.onLogout}>
              {user.logoutLabel}
            </MenuButton>
          </Menu>
        </div>
      </header>
      <main className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-6">{children}</main>
    </div>
  );
}
